import { useEffect, useState } from "react";
import AppButton from "../common/AppButton";
import AppForm from "../common/AppForm";
import AppModal from "../common/AppModal";
import AppTable from "../common/AppTable";
import AppSwitchButton from "../common/AppSwitchButton";

import { useAsync, usePagination } from "../../hooks";
import {
  deleteUser,
  enableUsers,
  getUser,
  patchUser,
  postUser,
} from "../../services";
import {
  userColumns,
  userDropDowns,
  userFields,
  userForm,
} from "../../constants";
import { userEditSchema, userSchema } from "../../utils/schema";
import { GetUser, UserData } from "../../interface";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { styled } from "@mui/material/styles";

const IconWrapper = styled("span")(() => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "space-between",
  position: "relative",
  width: 28,
  height: 28,
  margin: "0 6px",
  "& .default-icon": { display: "block" },
  "& .hover-icon": {
    display: "none",
    position: "absolute",
    top: 0,
    left: 0,
  },
  "&:hover .default-icon": { display: "none" },
  "&:hover .hover-icon": { display: "block" },
}));

const UsersTable: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [search, setSearch] = useState("");
  const { meta, setMeta, handleChangePage, handleChangeRowsPerPage } =
    usePagination(5, "usersTable");

  const {
    loading: loadingGet,
    error: errorGet,
    execute: executeGet,
  } = useAsync<GetUser, [number, number, string]>();
  const {
    loading: loadingPost,
    execute: executePost,
    error: errorPost,
  } = useAsync<string, [UserData]>();
  const {
    loading: loadingPatch,
    execute: executePatch,
    error: errorPatch,
  } = useAsync<string, [number, Partial<UserData>]>();
  const {
    loading: loadingDelete,
    execute: executeDelete,
    error: errorDelete,
  } = useAsync<string, [number]>();

  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "delete" | null>(
    null
  );
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<number | null>(null);

  const isLoading = loadingGet || loadingPost || loadingPatch || loadingDelete;
  const isError = errorGet || errorPost || errorPatch || errorDelete;

  useEffect(() => {
    executeGet(getUser, meta.currentPage, meta.pageSize, search).then(
      (response) => {
        setUsers(response.data);
        setMeta(response.meta);
      }
    );
  }, [meta.currentPage, meta.pageSize, search, executeGet, setMeta]);

  const handleCloseModal = () => {
    setModalMode(null);
    setSelectedUser(null);
    setDeleteUserId(null);
    setOpenModal(false);
  };

  const handleOpenAddModal = () => {
    setModalMode("add");
    setSelectedUser(null);
    setOpenModal(true);
  };

  const handleOpenEditModal = (user: UserData) => {
    setModalMode("edit");
    setSelectedUser(user);
    setOpenModal(true);
  };

  const handleOpenDeleteModal = (id: number) => {
    setModalMode("delete");
    setDeleteUserId(id);
    setOpenModal(true);
  };

  const handleSubmitUser = async (values: UserData) => {
    try {
      if (modalMode === "edit" && selectedUser) {
        handleCloseModal();
        const message = await executePatch(patchUser, selectedUser.id!, values);
        console.log("✅ User updated:", message);
        setUsers((prev) =>
          prev.map((user) =>
            user.id === selectedUser.id ? { ...user, ...values } : user
          )
        );
      } else if (modalMode === "add") {
        handleCloseModal();
        const message = await executePost(postUser, values);
        console.log("✅ User Added:", message);

        // 🔄 re-fetch users after add
        const response = await executeGet(
          getUser,
          meta.currentPage,
          meta.pageSize,
          search
        );
        setUsers(response.data);
        setMeta(response.meta);
      }
    } catch (err) {
      console.error("❌ Failed to save user", err);
    }
  };

  const handleDelete = async () => {
    if (!deleteUserId) return;

    handleCloseModal();

    try {
      const message = await executeDelete(deleteUser, deleteUserId);
      console.log("✅ Deleted:", message);
      setUsers((prev) => prev.filter((user) => user.id !== deleteUserId));
      setMeta((prev) => {
        const newTotalRecords = prev.totalRecords - 1;
        const newTotalPages = Math.ceil(newTotalRecords / prev.pageSize);
        const newCurrentPage = Math.min(prev.currentPage, newTotalPages || 1);
        return {
          ...prev,
          totalRecords: newTotalRecords,
          totalPages: newTotalPages,
          currentPage: newCurrentPage,
        };
      });
      setDeleteUserId(null);
    } catch (err) {
      console.error("❌ Delete error:", err);
    }
  };

  const handleSwitchButton = async (id: number, checked: boolean) => {
    setUsers((prev) =>
      prev.map((user) =>
        user.id === id ? { ...user, isEnabled: checked } : user
      )
    );
    try {
      await executePatch(enableUsers, id, { isEnabled: checked });
    } catch (err) {
      console.error(err);
      setUsers((prev) =>
        prev.map((user) =>
          user.id === id ? { ...user, isEnabled: !checked } : user
        )
      );
    }
  };

  const extendedColumns = [
    ...userColumns,
    {
      id: "actions",
      label: "Actions",
      render: (user: UserData) => (
        <>
          <IconWrapper>
            <EditOutlinedIcon
              className="default-icon"
              sx={{ color: "#1976d2", cursor: "pointer" }}
            />
            <EditIcon
              className="hover-icon"
              onClick={() => handleOpenEditModal(user)}
              sx={{ color: "#1976d2", cursor: "pointer" }}
            />
          </IconWrapper>
          <IconWrapper>
            <DeleteOutlinedIcon
              className="default-icon"
              sx={{ color: "#d32f2f", cursor: "pointer" }}
            />
            <DeleteIcon
              className="hover-icon"
              onClick={() => handleOpenDeleteModal(user.id!)}
              sx={{ color: "#d32f2f", cursor: "pointer" }}
            />
          </IconWrapper>
          <IconWrapper>
            <AppSwitchButton
              checked={user.isEnabled}
              onChange={(e) => handleSwitchButton(user.id!, e.target.checked)}
            />
          </IconWrapper>
        </>
      ),
    },
  ];

  return (
    <>
      {modalMode === "add" || modalMode === "edit" ? (
        <AppModal
          title={modalMode === "edit" ? "Edit User" : "Add User"}
          open={openModal}
          handleClose={handleCloseModal}
          action={modalMode === "edit" ? "Update User" : "Add User"}
        >
          <AppForm
            inputFields={userForm}
            initialValues={selectedUser || userFields}
            schema={modalMode === "edit" ? userEditSchema : userSchema}
            options
            handleSubmit={handleSubmitUser}
            dropDownLabels="User Role"
            dropDownOptions={userDropDowns}
          />
        </AppModal>
      ) : null}

      {modalMode === "delete" && (
        <AppModal
          title="Delete User"
          open={openModal}
          handleClose={handleCloseModal}
          onSubmit={handleDelete}
          action="Delete"
          color="#dc3545"
        >
          <p>
            Are you sure you want to delete this user? This action cannot be
            undone.
          </p>
        </AppModal>
      )}

      <div className="mb-3 d-flex justify-content-end">
        <AppButton
          label="Add User"
          size="sm"
          fullWidth={false}
          onClick={handleOpenAddModal}
        />
      </div>

      <AppTable<UserData>
        title="Users Table"
        columns={extendedColumns}
        renderRowKey={(user) => user.id ?? Math.random()}
        loading={isLoading}
        error={isError}
        count={meta.totalRecords}
        page={meta.currentPage - 1}
        onPageChange={handleChangePage}
        rowsPerPage={meta.pageSize}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rows={users}
        searchValue={search}
        onSearchChange={(value) => {
          setMeta({ ...meta, currentPage: 1 });
          setSearch(value);
        }}
        searchPlaceholder="Search User..."
      />
    </>
  );
};

export default UsersTable;
